import React from 'react'
import styled from 'styled-components'

import { LargeP, P, MonoP, SmallP } from './text'
import { colors, fonts } from '../../styles/variables'
import { media } from '../../styles/util'

const ArrowSVG = styled.svg`
  margin-left: 9px;
  fill: inherit;
  margin-bottom: -9px;
  ${media.mobile`
    height: 26px;
    width: 26px;
  `}
  ${media.tablet`
    height: 18px;
    width: 18px;
  `}
  ${media.desktop`
    height: 24px;
    width: 24px;
  `}
`

const ArrowSVGPath = styled.path`
  fill: inherit;
`

const Button = styled('button')<{ bg?: string; color?: string; noScale?: boolean }>`
  background-color: ${(props) => props.bg || colors.navy};
  border: 1px solid ${(props) => props.bg || colors.navy};
  color: ${(props) => props.color || colors.white};
  fill: ${(props) => props.color || colors.white};
  border-radius: 60px;
  outline: none;
  margin: 0;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  transition: 100ms all ease-in-out;
  white-space: pre-wrap;
  width: fit-content;

  ${media.mobile`
    padding: 12px 24px 15px 24px;
  `}
  ${media.desktop`
    padding: 15px 36px 17px 36px;
  `}

  &:hover {
    ${media.desktop`
      transform: ${(props: { noScale?: boolean }) => (props.noScale ? 'none' : 'scale(1.07)')};
    `}
  }
`

const StretchBtn = styled(Button)`
  ${media.mobile`
    width: 100%;
    padding: 12px 27px 14px 27px;
  `}
  ${media.tablet`
    width: fit-content;
    padding: 12px 27px;
    font-size: 26px;
  `}
`

const Text = styled(LargeP)`
  display: inline-block;
  line-height: 1 !important;
  ${media.mobile`
    font-size: 18px;
  `}
  ${media.tablet`
    font-size: 24px;
  `}
  ${media.desktop`
    font-size: 24px;
  `}
`

const AllCapsText = styled(P)`
  font-family: ${fonts.larsseit.bold};
  font-weight: 700;
`

const StretchText = styled(Text)`
  font-family: ${fonts.larsseit.regular};
  font-size: 26px !important;
`

const SecondaryBtn = styled(Button)`
  background-color: ${({ bg }) => bg || 'transparent'};
  border: 1px solid ${(props) => (props.bg ? props.bg : props.color || colors.navy)};
  color: ${(props) => props.color || colors.navy};

  transition: 150ms all ease-in-out;

  ${media.desktop`
    padding: 13px 20px 15px 20px;
  `}

  & ${ArrowSVGPath} {
    fill: ${(props) => props.color || colors.navy};
  }
`

const SmallSecondaryBtn = styled(SecondaryBtn)`
  ${media.mobile`
    padding: 9px 15px;
  `}
  ${media.tablet`
    height: 45px;
  `}
  ${media.desktop`
    padding: 9px 15px;
  `}
`

const NavBtn = styled(Button)<{ navDrawer?: boolean; dark?: boolean; color?: string }>`
  border-radius: 50px;
  border-color: ${(props) => props.color || colors.navy};
  background-color: ${(props) =>
    props.color ? props.color : props.navDrawer ? colors.white : 'transparent'};
  transition: 200ms all ease-in-out;
  height: fit-content;
  width: fit-content;
  ${media.mobile`
    padding: 9px 24px 7px 24px;
    margin-right: 12px;
  `}
  ${media.tablet`
    height: 45px;
  `}
  ${media.desktop`
    padding: 15px 30px 13px 30px;
    margin-right: 20px;
  `}
  &:hover {
    transform: scale(1);
    background-color: ${(props) => (props.dark ? colors.white : colors.navy)};
    border-color: ${(props) => (props.navDrawer ? colors.white : colors.navy)};
    ${media.desktop`
      padding: 15px 30px 13px 30px;
      margin-right: 20px;
    `}
    & p {
      font-size: 16px !important;
      color: ${(props) => (props.dark ? colors.navy : colors.lightText)};
    }
  }
  & p {
    font-size: 16px !important;
    color: ${colors.navy};
  }
`

const ActionBtn = styled(SecondaryBtn)`
  color: ${colors.slate};
  border: 1px solid ${colors.slate};
  transition: 150ms all ease-in-out;
  line-height: 1 !important;
  margin: 6px;
  &:hover {
    transform: scale(1);
    background-color: ${colors.navy};
    color: ${colors.lightText};
    border-color: ${colors.navy};
    padding: 9px 12px;
  }
  ${media.mobile`
    padding: 9px 12px;
  `}
  ${media.desktop`
    padding: 9px 12px;
  `}
`

const ConsultationBtn = styled(SecondaryBtn)`
  ${media.mobile`
    padding: 12px 24px 15px 24px;
  `}
  ${media.desktop`
    padding: 10px 13px 10px 17px;
  `}
`

const SquareButton = styled(Button)`
  border-radius: 10px;
  width: 100%;
  gap: 4px;
`

interface ButtonProps {
  copy: string
  type?: 'button' | 'submit' | 'reset' | undefined
  onClick?: (props: any) => void
  bg?: string
  color?: string
  arrow?: boolean
  dark?: boolean
  navDrawer?: boolean
  noScale?: boolean
  className?: string
}

const ArrowIcon = () => (
  <ArrowSVG viewBox="0 0 20 15" fill="none" xmlns="http://www.w3.org/2000/svg">
    <ArrowSVGPath d="M6.8 0L13.5 6.1L6.8 12.2L5.8 11L10.2 7H0V5.2H10.2L5.7 1.2L6.8 0Z" />
  </ArrowSVG>
)

const LockIcon = () => (
  <svg width="16" height="18" viewBox="0 0 79 90" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M39.5 68.5714C42.119 68.5714 44.6308 67.6684 46.4827 66.0609C48.3346 64.4535 49.375 62.2733 49.375 60C49.375 55.2429 44.9313 51.4286 39.5 51.4286C36.881 51.4286 34.3692 52.3316 32.5173 53.9391C30.6654 55.5465 29.625 57.7267 29.625 60C29.625 62.2733 30.6654 64.4535 32.5173 66.0609C34.3692 67.6684 36.881 68.5714 39.5 68.5714ZM69.125 30C71.744 30 74.2558 30.9031 76.1077 32.5105C77.9596 34.118 79 36.2981 79 38.5714V81.4286C79 83.7019 77.9596 85.882 76.1077 87.4895C74.2558 89.0969 71.744 90 69.125 90H9.875C7.25599 90 4.74424 89.0969 2.89232 87.4895C1.0404 85.882 0 83.7019 0 81.4286V38.5714C0 33.8143 4.44375 30 9.875 30H14.8125V21.4286C14.8125 15.7454 17.4135 10.2949 22.0433 6.27628C26.6731 2.25765 32.9525 0 39.5 0C42.742 0 45.9523 0.554266 48.9475 1.63115C51.9427 2.70804 54.6643 4.28646 56.9567 6.27628C59.2491 8.26611 61.0676 10.6284 62.3083 13.2282C63.5489 15.828 64.1875 18.6145 64.1875 21.4286V30H69.125ZM39.5 8.57143C35.5715 8.57143 31.8039 9.92602 29.026 12.3372C26.2481 14.7484 24.6875 18.0186 24.6875 21.4286V30H54.3125V21.4286C54.3125 18.0186 52.7519 14.7484 49.974 12.3372C47.1961 9.92602 43.4285 8.57143 39.5 8.57143Z"
      fill="white"
    />
  </svg>
)

export const CheckoutButton = ({ copy }) => {
  return (
    <SquareButton bg={colors.orange} color={colors.white} noScale={true}>
      <AllCapsText>{copy}</AllCapsText>
      <LockIcon />
    </SquareButton>
  )
}

export const PrimaryButton: React.FC<ButtonProps> = ({
  copy,
  onClick,
  arrow,
  bg,
  color,
  type,
  noScale = false,
  className
}) => {
  return (
    <Button
      type={type || 'button'}
      onClick={onClick}
      bg={bg}
      color={color}
      noScale={noScale}
      className={className}
    >
      <Text>{copy}</Text>
      {arrow && <ArrowIcon />}
    </Button>
  )
}

export const StretchCTAButton: React.FC<ButtonProps> = ({
  copy,
  onClick,
  arrow,
  bg,
  color,
  type,
  className
}) => {
  return (
    <StretchBtn
      type={type || 'button'}
      onClick={onClick}
      bg={bg}
      color={color}
      className={className}
    >
      <StretchText>{copy}</StretchText>
      {arrow && <ArrowIcon />}
    </StretchBtn>
  )
}

export const SecondaryButton: React.FC<ButtonProps> = ({
  copy,
  onClick,
  arrow,
  type,
  color,
  bg,
  className
}) => {
  return (
    <SecondaryBtn
      type={type || 'button'}
      bg={bg}
      color={color}
      onClick={onClick}
      className={className}
    >
      <Text>{copy}</Text>
      {arrow && <ArrowIcon />}
    </SecondaryBtn>
  )
}

export const SmallSecondaryButton: React.FC<ButtonProps> = ({
  copy,
  onClick,
  arrow,
  type,
  color,
  bg
}) => {
  return (
    <SmallSecondaryBtn bg={bg} type={type || 'button'} color={color} onClick={onClick}>
      <P>{copy}</P>
      {arrow && <ArrowIcon />}
    </SmallSecondaryBtn>
  )
}

export const NavButton: React.FC<ButtonProps> = ({
  copy,
  onClick,
  color,
  navDrawer,
  type,
  arrow
}) => {
  return (
    <NavBtn type={type || 'button'} onClick={onClick} color={color} navDrawer={navDrawer}>
      <MonoP>{copy}</MonoP>
      {arrow && <ArrowIcon />}
    </NavBtn>
  )
}

export const ActionButton: React.FC<ButtonProps> = ({ copy, onClick, type }) => {
  return (
    <ActionBtn type={type || 'button'} onClick={onClick}>
      <MonoP>{copy}</MonoP>
    </ActionBtn>
  )
}

export const ConsultationButton: React.FC<ButtonProps> = ({ copy, onClick, arrow, type }) => {
  return (
    <ConsultationBtn type={type || 'button'} onClick={onClick}>
      <Text>{copy}</Text>
      {arrow && <ArrowIcon />}
    </ConsultationBtn>
  )
}
